import { useQuery } from "@tanstack/react-query";
import {
  scheduleApi,
  scheduleKeys,
  type BookingPolicyResponse,
  type ScheduleResponse,
  type ServiceResponse,
} from "./scheduleApi";

export type Bookability = "OK" | "NO_HOURS" | "NO_ONLINE_SERVICE" | "ONLINE_BOOKING_OFF";

/**
 * Can a guest actually book online? `HOURS = COMPLETE` can't answer this: any saved schedule sets it,
 * including an all-closed week. Slots need a window on an online-bookable service while the venue-wide
 * switch is on (AvailabilityEngine), so this reads those three facts. Hours come first because they are
 * the common gap and the only one the wizard can fix inline.
 */
export function computeBookability(
  services: ServiceResponse[],
  schedule: ScheduleResponse,
  policy: BookingPolicyResponse,
): Bookability {
  if (schedule.windows.length === 0) return "NO_HOURS";
  const online = new Set(services.filter((s) => s.onlineBookable).map((s) => s.id));
  if (!schedule.windows.some((w) => online.has(w.serviceId))) return "NO_ONLINE_SERVICE";
  return policy.onlineBookingEnabled ? "OK" : "ONLINE_BOOKING_OFF";
}

export interface BookableTimes {
  isLoading: boolean;
  /** Null while loading, or when a read failed: an outage must not invent a warning or block go-live. */
  bookability: Bookability | null;
}

/** The venue's bookability, read through the same query keys the schedule editors invalidate. */
export function useBookableTimes(venueId: string): BookableTimes {
  const services = useQuery({
    queryKey: scheduleKeys.services(venueId),
    queryFn: () => scheduleApi.listServices(venueId),
  });
  const schedule = useQuery({
    queryKey: scheduleKeys.schedule(venueId),
    queryFn: () => scheduleApi.getSchedule(venueId),
  });
  const policy = useQuery({
    queryKey: scheduleKeys.policy(venueId),
    queryFn: () => scheduleApi.getPolicy(venueId),
  });
  return {
    isLoading: services.isLoading || schedule.isLoading || policy.isLoading,
    bookability:
      services.data && schedule.data && policy.data
        ? computeBookability(services.data, schedule.data, policy.data)
        : null,
  };
}
